import { useMemo, useState } from "react";
import {
  Card,
  Notice,
  Select,
  SelectItem,
  AnalyticalTable,
  type AnalyticalColumn,
} from "@conuti-das/prince-ui";
import { Flex } from "@/ui-prince";
import changelogData from "@/data/generated/changelog.json";
import type { ChangelogData, ChangelogEntry } from "@/types/changelog";
import {
  type DiffTone,
  PROV_LABEL,
  PROV_TONE,
  TYPE_LABEL,
  TYPE_ORDER,
  TYPE_SYM,
  entryId,
  toneOf,
} from "@/lib/changelogLabels";
import { ChangelogDetailDialog } from "@/components/changelog/ChangelogDetailDialog";
import {
  type ChangelogReviewStatus,
  reviewStatusOf,
  useChangelogReviewState,
} from "@/hooks/useChangelogReviewState";
import "./changelog.css";

/** Tabellenzeile: Eintrag plus lokaler Review-Status (Status-Spalte). */
type ChangelogRow = ChangelogEntry & {
  review_status: ChangelogReviewStatus;
  review_jira_key?: string;
};

const REVIEW_TONE: Record<ChangelogReviewStatus, DiffTone> = {
  offen: "neutral",
  approved: "added",
  rejected: "removed",
  "in-jira": "added",
};

/**
 * Exported for unit tests: defensively resolves the display ID of an entry.
 * Group-header rows produced by AnalyticalTable lack `row.original`, so we
 * guard against undefined (historical crash source).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function IdCell(props: any) {
  const original = props?.row?.original as ChangelogEntry | undefined;
  return <>{original?.aend_id ?? original?.doc_diff_id ?? ""}</>;
}

function Chip({
  tone,
  sym,
  label,
  count,
}: {
  tone: DiffTone | "total";
  sym?: string;
  label: string;
  count: number;
}) {
  return (
    <span className={`cl-chip cl-chip-${tone}`} title={label}>
      {sym && <span className="cl-chip-sym">{sym}</span>}
      <span>{label}</span>
      <span className="cl-chip-count">{count}</span>
    </span>
  );
}

/** Single-line, ellipsis-truncated cell with the full value as tooltip. */
function Trunc({ value, className }: { value: string; className?: string }) {
  const v = (value ?? "").trim();
  if (!v) return <span className="cl-empty-cell">leer</span>;
  return (
    <span className={`cl-trunc ${className ?? ""}`} title={value}>
      {v.replace(/\s+/g, " ")}
    </span>
  );
}

function TypeBadge({ changeType }: { changeType: string }) {
  return (
    <span className={`cl-badge cl-badge-${toneOf(changeType)}`}>
      {TYPE_SYM[changeType] ?? ""} {TYPE_LABEL[changeType] ?? changeType}
    </span>
  );
}

export function ChangelogPage() {
  const data = changelogData as ChangelogData;
  const allRows = data.entries;

  const [doctype, setDoctype] = useState("__all__");
  const [format, setFormat] = useState("__all__");
  const [type, setType] = useState("__all__");
  const [provenance, setProvenance] = useState("__all__");
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState<ChangelogEntry | null>(null);
  const { getDecision, setDecision, clearDecision, setJiraKey } = useChangelogReviewState(
    data.run_id ?? "",
  );

  // Distinct filter option values.
  const doctypes = useMemo(
    () => Array.from(new Set(allRows.map((r) => r.doctype))).filter(Boolean).sort(),
    [allRows],
  );
  const formats = useMemo(
    () => Array.from(new Set(allRows.map((r) => r.format ?? ""))).filter(Boolean).sort(),
    [allRows],
  );
  const types = useMemo(() => {
    const present = new Set(allRows.map((r) => r.change_type));
    return TYPE_ORDER.filter((t) => present.has(t));
  }, [allRows]);
  const provenances = useMemo(
    () => Array.from(new Set(allRows.map((r) => r.provenance))).filter(Boolean).sort(),
    [allRows],
  );

  // Counts per change_type over the *unfiltered* set (header stat chips).
  const typeCounts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const r of allRows) c[r.change_type] = (c[r.change_type] ?? 0) + 1;
    return c;
  }, [allRows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = allRows;
    if (doctype !== "__all__") rows = rows.filter((r) => r.doctype === doctype);
    if (format !== "__all__") rows = rows.filter((r) => (r.format ?? "") === format);
    if (type !== "__all__") rows = rows.filter((r) => r.change_type === type);
    if (provenance !== "__all__") rows = rows.filter((r) => r.provenance === provenance);
    if (q) {
      rows = rows.filter((r) =>
        [
          r.ort,
          r.before,
          r.after,
          r.reason,
          r.format ?? "",
          r.pruefid ?? "",
          entryId(r),
        ]
          .join("")
          .toLowerCase()
          .includes(q),
      );
    }
    return rows;
  }, [allRows, doctype, format, type, provenance, search]);

  const rowsWithStatus = useMemo<ChangelogRow[]>(
    () =>
      filtered.map((e) => {
        const d = getDecision(e);
        return { ...e, review_status: reviewStatusOf(d), review_jira_key: d?.jira_key };
      }),
    [filtered, getDecision],
  );

  const columns = useMemo<AnalyticalColumn<ChangelogRow>[]>(
    () => [
      {
        header: "Status",
        accessorKey: "review_status",
        width: "100px",
        cellRender: (row) => (
          <span
            className={`cl-badge cl-badge-${REVIEW_TONE[row.review_status]}`}
            title={row.review_jira_key}
          >
            {row.review_status}
          </span>
        ),
      },
      {
        header: "Herkunft",
        accessorKey: "provenance",
        width: "100px",
        cellRender: (row) =>
          row.provenance ? (
            <span className={`cl-badge cl-badge-${PROV_TONE[row.provenance] ?? "neutral"}`}>
              {PROV_LABEL[row.provenance] ?? row.provenance}
            </span>
          ) : null,
      },
      {
        header: "Doctype",
        accessorKey: "doctype",
        width: "80px",
        cellRender: (row) => (
          <span className="cl-mono">{(row.doctype ?? "").toUpperCase()}</span>
        ),
      },
      { header: "Format", accessorKey: "format", width: "100px" },
      {
        header: "Prüfi",
        accessorKey: "pruefid",
        width: "75px",
        cellRender: (row) => <span className="cl-mono">{row.pruefid ?? ""}</span>,
      },
      {
        header: "Typ",
        accessorKey: "change_type",
        width: "120px",
        cellRender: (row) => (row.change_type ? <TypeBadge changeType={row.change_type} /> : null),
      },
      {
        header: "Ort",
        accessorKey: "ort",
        width: "200px",
        cellRender: (row) => <Trunc value={row.ort ?? ""} />,
      },
      {
        header: "Bisher",
        accessorKey: "before",
        width: "240px",
        cellRender: (row) => <Trunc value={row.before ?? ""} className="cl-trunc-before" />,
      },
      {
        header: "Neu",
        accessorKey: "after",
        width: "240px",
        cellRender: (row) => <Trunc value={row.after ?? ""} className="cl-trunc-after" />,
      },
      {
        header: "Grund",
        accessorKey: "reason",
        width: "220px",
        cellRender: (row) => <Trunc value={row.reason ?? ""} />,
      },
      {
        header: "ID",
        accessorKey: "aend_id",
        width: "100px",
        cellRender: (row) => <>{row.aend_id ?? row.doc_diff_id ?? ""}</>,
      },
      {
        header: "FV",
        accessorKey: "formatversion",
        width: "75px",
        cellRender: (row) => <span className="cl-mono">{row.formatversion ?? ""}</span>,
      },
    ],
    [],
  );

  if (allRows.length === 0) {
    return (
      <div className="page changelog-page">
        <div className="page-header">
          <h2 className="page-title">Änderungen</h2>
        </div>
        <Notice tone="info">
          Noch kein <code>/erzeuge-doku-diff</code>-Lauf gefunden. Erst einen Lauf
          erzeugen, dann <code>npm run data</code>.
        </Notice>
      </div>
    );
  }

  return (
    <div className="page changelog-page">
      <div className="page-header">
        <h2 className="page-title">Änderungen</h2>
        <div className="page-subtitle">
          {allRows.length} Einträge · Lauf {data.run_id} · FV{" "}
          {data.formatversions.join(", ")}
        </div>
        <div className="cl-chips">
          <Chip tone="total" label="Gesamt" count={allRows.length} />
          {TYPE_ORDER.filter((t) => typeCounts[t]).map((t) => (
            <Chip
              key={t}
              tone={toneOf(t)}
              sym={TYPE_SYM[t]}
              label={TYPE_LABEL[t] ?? t}
              count={typeCounts[t]}
            />
          ))}
        </div>
      </div>

      <Card>
        <Flex
          style={{
            padding: "var(--s-3) var(--s-4)",
            gap: "var(--s-4)",
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <Flex style={{ alignItems: "center", gap: "var(--s-2)" }}>
            <label className="prn-field-label" htmlFor="cl-doctype">Doctype</label>
            <Select
              id="cl-doctype"
              selectedKey={doctype}
              onSelectionChange={(key) => setDoctype(key != null ? String(key) : "__all__")}
            >
              <SelectItem id="__all__">Alle</SelectItem>
              {doctypes.map((d) => (
                <SelectItem key={d} id={d}>{d.toUpperCase()}</SelectItem>
              ))}
            </Select>
          </Flex>

          <Flex style={{ alignItems: "center", gap: "var(--s-2)" }}>
            <label className="prn-field-label" htmlFor="cl-format">Format</label>
            <Select
              id="cl-format"
              selectedKey={format}
              onSelectionChange={(key) => setFormat(key != null ? String(key) : "__all__")}
            >
              <SelectItem id="__all__">Alle Formate</SelectItem>
              {formats.map((f) => (
                <SelectItem key={f} id={f}>{f}</SelectItem>
              ))}
            </Select>
          </Flex>

          <Flex style={{ alignItems: "center", gap: "var(--s-2)" }}>
            <label className="prn-field-label" htmlFor="cl-type">Typ</label>
            <Select
              id="cl-type"
              selectedKey={type}
              onSelectionChange={(key) => setType(key != null ? String(key) : "__all__")}
            >
              <SelectItem id="__all__">Alle Typen</SelectItem>
              {types.map((t) => (
                <SelectItem key={t} id={t}>{TYPE_LABEL[t] ?? t}</SelectItem>
              ))}
            </Select>
          </Flex>

          <Flex style={{ alignItems: "center", gap: "var(--s-2)" }}>
            <label className="prn-field-label" htmlFor="cl-prov">Herkunft</label>
            <Select
              id="cl-prov"
              selectedKey={provenance}
              onSelectionChange={(key) => setProvenance(key != null ? String(key) : "__all__")}
            >
              <SelectItem id="__all__">Alle</SelectItem>
              {provenances.map((p) => (
                <SelectItem key={p} id={p}>{PROV_LABEL[p] ?? p}</SelectItem>
              ))}
            </Select>
          </Flex>

          <Flex
            style={{ alignItems: "center", gap: "var(--s-2)", flex: "1 1 240px" }}
          >
            <label className="prn-field-label" htmlFor="cl-search">Suche</label>
            <input
              id="cl-search"
              className="prn-input"
              value={search}
              placeholder="Ort, Text, Grund, ID ..."
              onChange={(e) => setSearch(e.target.value ?? "")}
              style={{ flex: 1 }}
            />
          </Flex>
        </Flex>
      </Card>

      <Card style={{ marginTop: "var(--s-3)" }}>
        <AnalyticalTable
          className="cl-analytical-table"
          data={rowsWithStatus}
          columns={columns}
          visibleRows={18}
          minRows={5}
          sortable
          filterable
          noDataText="Keine Änderungen in der aktuellen Auswahl."
          onRowClick={({ row }) => setDetail(row)}
        />
        <div className="cl-table-foot" style={{ padding: "var(--s-2) var(--s-4)" }}>
          {filtered.length} Einträge — Klick auf eine Zeile öffnet die Details.
        </div>
      </Card>

      {detail && (
        <ChangelogDetailDialog
          entry={detail}
          runId={data.run_id ?? ""}
          decision={getDecision(detail)}
          onClose={() => setDetail(null)}
          onApprove={() => { setDecision(detail, { state: "approved" }); setDetail(null); }}
          onReject={(reason) => { setDecision(detail, { state: "rejected", reason }); setDetail(null); }}
          onReset={() => { clearDecision(detail); setDetail(null); }}
          onJiraCreated={(jiraKey) => setJiraKey(detail, jiraKey)}
        />
      )}
    </div>
  );
}
